<template>
  <div class="h-full overflow-auto bg-neutral-100">
    <div class="mb-20">
      <!-- content -->
      <div class="mx-auto mb-5 mt-5 max-w-5xl px-2">
        <div
          class="mx-0 mb-4 mt-4 bg-sky-50 shadow sm:mx-5 sm:my-5 sm:rounded-lg md:mx-8 md:my-8 xl:mx-auto xl:max-w-5xl"
        >
          <!-- Card Content goes here -->
          <div class="justify-between px-4 py-5 sm:p-10 lg:flex">
            <div>
              <h1 class="text-2xl font-extrabold">
                {{ appSettingsStore.applicationTitle }}
                <span class="font-semibold">({{ appSettingsStore.pageTitleBase }})</span>
              </h1>
              <h2 class="mt-2 text-xl text-neutral-500 md:mt-1">
                <span class="font-semibold">Welcome, {{ userRef.firstName }}</span>
              </h2>
              <div class="prose prose-neutral mt-4 prose-li:marker:text-neutral-400 md:mt-5">
                <p>(Home Internal)</p>
              </div>
            </div>
            <div class="mt-6 items-start justify-start gap-5 space-y-5 sm:flex sm:space-y-0 lg:mt-0">
              <LogoutButton label="Sign Out" :as="BaseButton" variant="priority-hollow" class="w-full sm:w-auto" />
            </div>
          </div>
        </div>
        <LandingCard v-if="complaintInsights">
          <template #default>
            <div
              class="mb-4 space-y-3 bg-gray-900 px-4 py-3 text-white shadow sm:mb-5 sm:rounded-lg sm:px-6 md:mb-8 xl:mx-auto"
            >
              <div class="text-center text-xl">{{ formatDate(currentDate, "MMMM yyyy") }}</div>

              <div class="grid grid-cols-2 gap-10 font-extrabold">
                <div class="col-span-1 text-center">
                  <div class="">
                    On-time Complaint Response
                    <span class="whitespace-nowrap font-semibold text-neutral-500">
                      (&lt; {{ complaintInsights.responseTargetHrs }} hr)
                    </span>
                  </div>
                  <div class="text-2xl font-extrabold">{{ complaintInsights.complaintsRespondedOnTimePctLabel }}</div>
                  <div class="font-semibold text-neutral-500">
                    ({{ complaintInsights.complaintsRespondedOnTime }} of
                    {{ complaintInsights.respondedComplaintCount }})
                  </div>
                </div>
                <div class="col-span-1 text-center">
                  <div class="">
                    On-time Complaint Resolution
                    <span class="whitespace-nowrap font-semibold text-neutral-500">
                      (&lt; {{ complaintInsights.resolutionTargetHrs }} hr)
                    </span>
                  </div>
                  <div class="text-2xl font-extrabold">{{ complaintInsights.complaintsResolvedOnTimePctLabel }}</div>
                  <div class="font-semibold text-neutral-500">
                    ({{ complaintInsights.complaintsResolvedOnTime }} of {{ complaintInsights.resolvedComplaintCount }})
                  </div>
                </div>
              </div>
            </div></template
          >
        </LandingCard>

        <LandingCard v-if="openComplaints">
          <template #title>
            <div class="text-lg font-medium text-neutral-500">
              Open Complaints
              <span
                class="inline-flex items-center rounded bg-neutral-300 px-2 py-0.5 text-xs font-medium text-neutral-800"
              >
                {{ openComplaints?.length }}
              </span>
              <span class="sr-only">{{ openComplaints?.length }} shown</span>
            </div>
          </template>
          <template #default
            ><div class="space-y-3 px-4 py-3 sm:px-6" v-if="openComplaints.length > 0">
              <div class="space-y-3 px-4 py-3 sm:px-6" v-for="complaint in openComplaints">
                <div>
                  <div class="mb-0.5 text-base font-medium text-sky-500">
                    <router-link class="hover:underline" :to="complaint.to">{{
                      tzDate.longDateTimeString(complaint.observedDateTime)
                    }}</router-link>
                  </div>
                  <div class="mb-0.5 text-base font-medium">
                    {{ formatDistanceToNow(complaint.observedDateTime, { addSuffix: true }) }}
                  </div>
                  <div class="text-sm leading-normal text-neutral-600">
                    {{ complaint.description }}
                    <span> (Complaint No. {{ complaint.id }})</span>
                  </div>
                  <div class="mb-0.5 text-sm">{{ complaint.assignedToObj?.name }}</div>
                </div>
              </div>
            </div></template
          >
        </LandingCard>

        <LandingCard v-if="pendingComplaints">
          <template #title>
            <div class="text-lg font-medium text-neutral-500">
              Pending Complaints
              <span
                class="inline-flex items-center rounded bg-neutral-300 px-2 py-0.5 text-xs font-medium text-neutral-800"
              >
                {{ pendingComplaints?.length }}
              </span>
              <span class="sr-only">{{ pendingComplaints?.length }} shown</span>
            </div>
          </template>
          <template #default
            ><div class="space-y-3 px-4 py-3 sm:px-6" v-if="pendingComplaints.length > 0">
              <div class="space-y-3 px-4 py-3 sm:px-6" v-for="complaint in pendingComplaints">
                <div>
                  <div class="mb-0.5 text-base font-medium text-sky-500">
                    <router-link class="hover:underline" :to="complaint.to">{{
                      tzDate.longDateTimeString(complaint.observedDateTime)
                    }}</router-link>
                  </div>
                  <div class="mb-0.5 text-base font-medium">
                    {{ formatDistanceToNow(complaint.observedDateTime, { addSuffix: true }) }}
                  </div>
                  <div class="text-sm leading-normal text-neutral-600">
                    {{ complaint.description }}
                    <span> (Complaint No. {{ complaint.id }})</span>
                  </div>
                  <div class="mb-0.5 text-sm">{{ complaint.takenByObj?.name }}</div>
                </div>
              </div>
            </div></template
          >
        </LandingCard>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, NoneFoundFlag, SkeletonButton, useAppSettingsStore } from "@modernary/mui/components/common";
import { useUser } from "@modernary/mui/utils/authentication";
import { LoginButton, LogoutButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { Authorize } from "@modernary/mui/utils/authorization";
import { routeNames } from "@/routes";
import { defineComponent, ref } from "vue";
import { FaceSmileIcon, PlusIcon } from "@heroicons/vue/24/solid";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/vue/20/solid";
import { PublicLandingCard } from "@modernary/mui/components/publicLanding";
import { useApi, type Paging } from "@modernary/mui/utils/api";
import { getComplaints, getComplaintsSummary } from "@/services/endpoints";
import { hide403s } from "@modernary/mui/utils/helpers";
import { adaptPagedResponse } from "@modernary/mui/components/lookup";
import complaintService from "@/views/complaint/complaintService";
import type { LandingCardEntity } from "./landing/landingCard";
import type { Complaint } from "@/views/complaint/complaint";
import { LE } from "@/constants/enums";
import { sysDate, tzDate } from "@modernary/mui/utils/filters";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import { formatDate, formatDistanceToNow } from "date-fns";

export default defineComponent({
  name: "HomeInternal",
  components: {
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    PlusIcon,
    ChevronRightIcon,
    MdrnAttribution,
    FaceSmileIcon,
    NoneFoundFlag,
    PhoneIcon,
    EnvelopeIcon,
    LogoutButton,
    Authorize,
    SkeletonButton,
    PublicLandingCard,
    LandingCardLinkSections,
    LandingCardLinkSection,
    LandingCard,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const openComplaints = ref<LandingCardEntity<Complaint>[]>();
    getComplaints({
      statusMurnIn: [LE.ComplaintStatus.Open],
      orderBy: [{ propertyName: "id", isDescending: true }],
      // take: 5,
    }).then(async (response) => {
      const data = (await adaptPagedResponse(response, complaintService.responseAdapter as any)) as unknown as Paging<
        LandingCardEntity<Complaint>
      >;
      data._items.forEach((item) => {
        item.to = { name: routeNames.complaint.view, params: { id: item.id } };
      });
      openComplaints.value = data._items;
    });

    const pendingComplaints = ref<LandingCardEntity<Complaint>[]>();
    getComplaints({
      statusMurnIn: [LE.ComplaintStatus.Pending],
      orderBy: [{ propertyName: "id", isDescending: true }],
    }).then(async (response) => {
      const data = (await adaptPagedResponse(response, complaintService.responseAdapter as any)) as unknown as Paging<
        LandingCardEntity<Complaint>
      >;
      data._items.forEach((item) => {
        item.to = { name: routeNames.complaint.view, params: { id: item.id } };
      });
      pendingComplaints.value = data._items;
    });

    const processingTargets = {
      response: 24,
      resolution: 72,
    };

    type ComplaintInsight = {
      //repsponse
      responseTargetHrs: number;
      complaintsRespondedOnTimePct: number;
      complaintsRespondedOnTimePctLabel: string;
      complaintsRespondedOnTime: number;
      respondedComplaintCount: number;
      //resolution
      resolutionTargetHrs: number;
      complaintsResolvedOnTimePct: number;
      complaintsResolvedOnTimePctLabel: string;
      resolvedComplaintCount: number;
      complaintsResolvedOnTime: number;
    };

    const { exec: execGetComplaintInsights } = useApi(getComplaintsSummary, {});

    const complaintInsights = ref<ComplaintInsight>();
    const currentDate = new Date();

    execGetComplaintInsights(
      {
        month: currentDate.getMonth() + 1,
        responseHours: processingTargets.response,
        closedHours: processingTargets.resolution,
      },
      hide403s(),
    ).then(async (result) => {
      if (!result) return;

      const complaintsRespondedOnTime = result.openTotal - result.openFail;
      const complaintsRespondedOnTimePct = Math.round((complaintsRespondedOnTime / result.openTotal) * 100);
      const complaintsResolvedOnTime = result.closedTotal - result.closedFail;
      const complaintsResolvedOnTimePct = Math.round((complaintsResolvedOnTime / result.closedTotal) * 100);

      const responseInsight: ComplaintInsight = {
        //response
        responseTargetHrs: processingTargets.response,
        complaintsRespondedOnTime: complaintsRespondedOnTime,
        complaintsRespondedOnTimePct: complaintsRespondedOnTimePct,
        complaintsRespondedOnTimePctLabel: isNaN(complaintsRespondedOnTimePct)
          ? "N/A"
          : `${complaintsRespondedOnTimePct}%1`,
        respondedComplaintCount: result.openTotal,

        //resolution
        resolutionTargetHrs: processingTargets.resolution,
        complaintsResolvedOnTime: complaintsResolvedOnTime,
        complaintsResolvedOnTimePct: complaintsResolvedOnTimePct,
        complaintsResolvedOnTimePctLabel: isNaN(complaintsResolvedOnTimePct)
          ? "N/A"
          : `${complaintsResolvedOnTimePct}%1`,
        resolvedComplaintCount: result.closedTotal,
      };

      complaintInsights.value = responseInsight;
    });

    return {
      userRef,
      routeNames,
      BaseButton,
      appSettingsStore,
      openComplaints,
      tzDate,
      formatDistanceToNow,
      complaintInsights,
      currentDate,
      sysDate,
      formatDate,
      pendingComplaints,
    };
  },
});
</script>
